import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import Reveal from "@/components/feature/Reveal";
import type { TreatmentCategoryItem, TreatmentMenuGroup, TreatmentMenuItem, TreatmentOptionItem } from "@/mocks/treatmentPages";

type Tone = "plain" | "tint";

const toneClass: Record<Tone, string> = {
  plain: "bg-background-50",
  tint: "bg-background-100",
};

type TreatmentSectionProps = {
  id: string;
  en: string;
  jp: string;
  lead?: string;
  tone?: Tone;
  children: ReactNode;
};

export function TreatmentSection({ id, en, jp, lead, tone = "plain", children }: TreatmentSectionProps) {
  return (
    <section id={id} className={`${toneClass[tone]} py-16 md:py-20 lg:py-24`}>
      <div className="u-container">
        <Reveal>
          <div className="flex flex-col gap-5 md:max-w-2xl">
            <span className="en-eyebrow text-[10px] text-secondary-700 md:text-[11px]">{en}</span>
            <span className="block h-px w-10 bg-accent-500" />
            <h2 className="font-heading text-[24px] leading-[1.55] tracking-[0.01em] text-foreground-950 md:text-[32px]">
              {jp}
            </h2>
            {lead ? (
              <p className="whitespace-pre-line text-[13px] leading-[2.1] text-foreground-700 md:text-sm">{lead}</p>
            ) : null}
          </div>
        </Reveal>
        <div className="mt-10 md:mt-14">{children}</div>
      </div>
    </section>
  );
}

/** ページ内目次（セクションへのリンク） */
export function TreatmentIndexNav({ items }: { items: { label: string; target: string }[] }) {
  return (
    <nav aria-label="ページ内目次" className="border-y border-background-300/70 bg-background-50">
      <div className="u-container">
        <ul className="no-scrollbar flex gap-x-7 overflow-x-auto py-5">
          {items.map((item) => (
            <li key={item.target} className="shrink-0">
              <a
                href={`#${item.target}`}
                className="group flex items-center gap-2 whitespace-nowrap text-[12px] text-foreground-600 transition-colors hover:text-foreground-950"
              >
                <span className="h-px w-3 bg-foreground-950/25 transition-all group-hover:w-5 group-hover:bg-accent-600" />
                {item.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}

/** 該当項目へジャンプするナビ（お悩み・部位・治療方法など） */
export function TreatmentJumpNav({
  title,
  lead,
  items,
}: {
  title: string;
  lead?: string;
  items: { label: string; target: string }[];
}) {
  return (
    <div className="border border-foreground-950/12 bg-background-50 p-7 md:p-9">
      <span className="en-eyebrow text-[10px] text-secondary-700 md:text-[11px]">{title}</span>
      {lead ? <p className="mt-4 max-w-xl text-[13px] leading-[2] text-foreground-700">{lead}</p> : null}
      <div className="mt-7 flex flex-wrap gap-2.5">
        {items.map((item) => (
          <a
            key={item.target}
            href={`#${item.target}`}
            className="group inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-2 text-[12.5px] text-foreground-800 transition-colors hover:border-accent-400 hover:bg-secondary-100/70"
          >
            {item.label}
            <i
              className="ri-arrow-down-line text-sm text-foreground-500 transition-colors group-hover:text-accent-700"
              aria-hidden
            />
          </a>
        ))}
      </div>
    </div>
  );
}

/** このようなお悩みの方へ */
export function ConcernList({ items }: { items: string[] }) {
  return (
    <ul className="grid grid-cols-1 gap-px border border-foreground-950/12 bg-foreground-950/12 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((item, index) => (
        <li key={item} className="flex items-start gap-4 bg-background-50 px-6 py-7">
          <span className="pt-0.5 font-label text-[11px] tracking-[0.2em] text-secondary-500">
            {String(index + 1).padStart(2, "0")}
          </span>
          <span className="font-heading text-[15px] leading-[1.8] text-foreground-900 md:text-base">{item}</span>
        </li>
      ))}
    </ul>
  );
}

/** 特徴 */
export function FeatureList({ items }: { items: { no: string; title: string; text: string }[] }) {
  return (
    <div className="border-t border-foreground-950/12">
      {items.map((feature) => (
        <div
          key={feature.no}
          className="grid grid-cols-1 gap-3 border-b border-foreground-950/12 py-7 md:grid-cols-[80px_1fr_1.4fr] md:items-baseline md:gap-8 md:py-9"
        >
          <span className="font-label text-[12px] tracking-[0.25em] text-secondary-600">{feature.no}</span>
          <h3 className="font-heading text-[17px] text-foreground-950 md:text-lg">{feature.title}</h3>
          <p className="text-[13px] leading-[2] text-foreground-700 md:text-sm">{feature.text}</p>
        </div>
      ))}
    </div>
  );
}

/** 治療方法・メニュー（group があればグループ表示） */
export function MenuList({ items, priceHref }: { items: TreatmentMenuItem[]; priceHref: string }) {
  const groups: { key: string; en?: string; items: TreatmentMenuItem[] }[] = [];
  items.forEach((item) => {
    const key = item.group && item.group !== item.title ? item.group : "";
    let group = groups.find((g) => g.key === key);
    if (!group) {
      group = { key, en: key ? item.groupEn : undefined, items: [] };
      groups.push(group);
    }
    group.items.push(item);
  });

  return (
    <div className="space-y-14">
      {groups.map((group) => (
        <div key={group.key || "default"}>
          {group.key ? (
            <div className="flex items-baseline gap-4 md:gap-5">
              {group.en ? (
                <span className="en-eyebrow text-[10px] text-secondary-700 md:text-[11px]">{group.en}</span>
              ) : null}
              <span className="font-heading text-lg text-foreground-950">{group.key}</span>
              <span className="h-px flex-1 bg-foreground-950/10" />
            </div>
          ) : null}

          <div
            className={`grid grid-cols-1 gap-px border border-foreground-950/12 bg-foreground-950/12 sm:grid-cols-2 ${
              group.key ? "mt-6" : ""
            }`}
          >
            {group.items.map((item) => (
              <div key={item.id} id={item.id} data-anchor className="flex flex-col bg-background-50 p-7">
                {!group.key && item.groupEn ? (
                  <span className="en-eyebrow mb-3 block text-[10px] text-secondary-700">{item.groupEn}</span>
                ) : null}
                {item.href ? (
                  <Link
                    to={item.href}
                    className="group/title inline-flex items-center gap-2 font-heading text-[17px] text-foreground-950 transition-colors hover:text-accent-700"
                  >
                    {item.title}
                    <i
                      className="ri-arrow-right-line text-[15px] text-foreground-400 transition-transform group-hover/title:translate-x-0.5"
                      aria-hidden
                    />
                  </Link>
                ) : item.anchor ? (
                  <a
                    href={`#${item.anchor}`}
                    className="group/title inline-flex items-center gap-2 font-heading text-[17px] text-foreground-950 transition-colors hover:text-accent-700"
                  >
                    {item.title}
                    <i
                      className="ri-arrow-down-line text-[15px] text-foreground-400 transition-transform group-hover/title:translate-y-0.5"
                      aria-hidden
                    />
                  </a>
                ) : (
                  <h3 className="font-heading text-[17px] text-foreground-950">{item.title}</h3>
                )}
                <p className="mt-3 text-[12.5px] leading-[1.95] text-foreground-700">{item.text}</p>
                {item.href && item.detailLabel ? (
                  <Link
                    to={item.href}
                    className="group/detail mt-6 inline-flex self-start items-center gap-2.5 whitespace-nowrap rounded-md border border-foreground-950/25 px-6 py-3 text-[12.5px] text-foreground-900 transition-colors hover:border-accent-500 hover:bg-secondary-100/70"
                  >
                    {item.detailLabel}
                    <i
                      className="ri-arrow-right-line text-[14px] text-foreground-500 transition-transform group-hover/detail:translate-x-0.5 group-hover/detail:text-accent-700"
                      aria-hidden
                    />
                  </Link>
                ) : null}
                {item.treatments && item.treatments.length > 0 ? (
                  <div className="mt-5 flex flex-wrap gap-2">
                    {item.treatments.map((treatment) => {
                      const chipClass =
                        "group/chip inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-3.5 py-1.5 text-[12px] text-foreground-700 transition-colors hover:border-accent-400 hover:bg-secondary-100/70 hover:text-foreground-950";
                      const arrow = (
                        <i
                          className="ri-arrow-right-line text-[13px] text-foreground-400 transition-transform group-hover/chip:translate-x-0.5"
                          aria-hidden
                        />
                      );
                      return treatment.slug ? (
                        <Link
                          key={treatment.id}
                          id={treatment.id}
                          data-anchor
                          data-treatment={treatment.slug}
                          to={`/skin/${treatment.slug}`}
                          className={chipClass}
                        >
                          {treatment.name}
                          {arrow}
                        </Link>
                      ) : (
                        <button
                          key={treatment.id}
                          id={treatment.id}
                          data-anchor
                          data-treatment={treatment.id}
                          type="button"
                          className={chipClass}
                        >
                          {treatment.name}
                          {arrow}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
                {item.priceAnchor ? (
                  <a
                    href={`#${item.priceAnchor}`}
                    className="group mt-auto inline-flex items-center gap-2 pt-6 text-[12px] text-foreground-800 transition-colors hover:text-accent-700"
                  >
                    料金を見る
                    <i className="ri-arrow-down-line transition-transform group-hover:translate-y-1" aria-hidden />
                  </a>
                ) : (
                  <Link
                    to={priceHref}
                    className="group mt-auto inline-flex items-center gap-2 pt-6 text-[12px] text-foreground-800 transition-colors hover:text-accent-700"
                  >
                    料金を見る
                    <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" aria-hidden />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** 番号付きの施術選択肢（例：2つの注入治療）。大きなカードにせず、細い罫線と余白で見せる。 */
export function TreatmentOptionsList({ items }: { items: TreatmentOptionItem[] }) {
  return (
    <div className="border-t border-foreground-950/12">
      {items.map((item) => (
        <div
          key={item.no}
          className="grid grid-cols-1 gap-6 border-b border-foreground-950/12 py-9 md:grid-cols-[72px_1fr] md:gap-10 md:py-12"
        >
          <span className="font-label text-[13px] tracking-[0.3em] text-secondary-600">{item.no}</span>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)] md:gap-14">
            <div>
              <span className="en-eyebrow text-[10px] text-secondary-700 md:text-[11px]">{item.en}</span>
              <h3 className="mt-4 font-heading text-[22px] leading-[1.5] text-foreground-950 md:text-[28px]">
                {item.jp}
              </h3>
            </div>
            <div className="flex flex-col items-start gap-6">
              <p className="text-[13px] leading-[2.1] text-foreground-700 md:text-sm">{item.text}</p>
              <Link
                to={item.href}
                className="group inline-flex items-center gap-2.5 whitespace-nowrap rounded-md border border-foreground-950/25 px-6 py-3 text-[12.5px] text-foreground-900 transition-colors hover:border-accent-500 hover:bg-secondary-100/70"
              >
                {item.ctaLabel}
                <i
                  className="ri-arrow-right-line text-[14px] text-foreground-500 transition-transform group-hover:translate-x-0.5 group-hover:text-accent-700"
                  aria-hidden
                />
              </Link>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * 施術一覧を施術方式ごと（4分類など）に整理して見せる。
 * 大きな角丸カードを使わず、大きな番号・英字/日本語の分類名・細い罫線・施術名・詳細リンクで構成する。
 */
export function TreatmentCategoryList({ groups }: { groups: TreatmentCategoryItem[] }) {
  return (
    <div className="border-t border-foreground-950/12">
      {groups.map((group) => (
        <div
          key={group.id}
          className="grid grid-cols-1 gap-7 border-b border-foreground-950/12 py-10 md:grid-cols-[104px_1fr] md:gap-12 md:py-14"
        >
          <span className="font-label text-[30px] leading-none tracking-[0.08em] text-secondary-500 md:text-[38px]">
            {group.no}
          </span>

          <div>
            <span className="en-eyebrow block text-[10px] text-secondary-700 md:text-[11px]">{group.en}</span>
            <h3 className="mt-3 font-heading text-[22px] leading-[1.5] text-foreground-950 md:text-[28px]">
              {group.jp}
            </h3>
            {group.lead ? (
              <p className="mt-4 max-w-2xl text-[13px] leading-[2] text-foreground-700 md:text-sm">
                {group.lead}
              </p>
            ) : null}

            <ul className="mt-8 border-t border-foreground-950/10">
              {group.items.map((item) => (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className="group grid grid-cols-1 gap-x-4 gap-y-3.5 border-b border-foreground-950/10 px-3 py-5 transition-colors hover:bg-secondary-100/50 md:-mx-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-center md:gap-x-10 md:gap-y-0 md:py-6"
                  >
                    <span className="flex min-w-0 flex-col gap-2 md:gap-2.5">
                      <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        <span className="font-heading text-[15.5px] text-foreground-950 transition-colors group-hover:text-accent-700 md:text-base">
                          {item.name}
                        </span>
                        {item.note ? (
                          <span className="text-[11px] leading-[1.7] tracking-[0.04em] text-foreground-500">
                            {item.note}
                          </span>
                        ) : null}
                      </span>
                      {item.summary ? (
                        <span className="max-w-2xl text-[12px] leading-[1.95] text-foreground-600 md:text-[12.5px] md:leading-[2]">
                          {item.summary}
                        </span>
                      ) : null}
                    </span>
                    <span className="flex shrink-0 items-center gap-2 self-start whitespace-nowrap text-[12.5px] leading-none text-foreground-800 transition-colors group-hover:text-accent-700 md:self-center md:border-l md:border-foreground-950/10 md:py-1 md:pl-8">
                      詳しく見る
                      <i
                        className="ri-arrow-right-line text-sm text-foreground-400 transition-transform group-hover:translate-x-0.5 group-hover:text-accent-700"
                        aria-hidden
                      />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ))}
    </div>
  );
}

/** カテゴリ（BOTOX / ヒアルロン酸 等）ごとの部位一覧 */
export function MenuGroupList({ groups }: { groups: TreatmentMenuGroup[] }) {
  return (
    <div className="space-y-16 md:space-y-20">
      {groups.map((group) => (
        <div key={group.id} id={group.id} data-anchor>
          <div className="flex items-baseline gap-4 md:gap-5">
            {group.en ? (
              <span className="en-eyebrow text-[10px] text-secondary-700 md:text-[11px]">{group.en}</span>
            ) : null}
            <span className="font-heading text-xl text-foreground-950 md:text-2xl">{group.jp}</span>
            <span className="h-px flex-1 bg-foreground-950/10" />
          </div>
          {group.lead ? (
            <p className="mt-5 max-w-2xl text-[13px] leading-[2] text-foreground-700">{group.lead}</p>
          ) : null}
          {group.href ? (
            <Link
              to={group.href}
              className="group/glink mt-5 inline-flex items-center gap-2 whitespace-nowrap text-[12.5px] text-foreground-800 transition-colors hover:text-accent-700"
            >
              {group.jp}の詳細を見る
              <i
                className="ri-arrow-right-line transition-transform group-hover/glink:translate-x-0.5"
                aria-hidden
              />
            </Link>
          ) : null}
          <div className="mt-7 grid grid-cols-2 gap-px border border-foreground-950/12 bg-foreground-950/12 sm:grid-cols-3 lg:grid-cols-4">
            {group.items.map((item) => (
              <button
                key={item.id}
                id={item.id}
                data-anchor
                data-treatment={item.id}
                type="button"
                className="group flex items-center justify-between gap-3 bg-background-50 px-5 py-6 text-left transition-colors hover:bg-secondary-100/60"
              >
                <span className="font-heading text-[15px] text-foreground-900 md:text-base">{item.name}</span>
                <i
                  className="ri-arrow-right-line text-base text-foreground-400 transition-all group-hover:translate-x-0.5 group-hover:text-accent-700"
                  aria-hidden
                />
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}